import * as Headless from '@headlessui/react';
import { AdjustmentsHorizontalIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import React from 'react';
import {
  DEFAULT_ANALYTICS_AVG_ACCOUNT_PREMIUM,
  DEFAULT_ANALYTICS_HOURLY_RATE
} from '~/components/settings/analytics/analytics-constants';

export type AnalyticsAssumptions = {
  hourlyRate?: number;
  avgAccountPremium?: number;
};

const TRIGGER_CLASS = clsx(
  'inline-flex items-center gap-2 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-1.5',
  'text-xs font-medium text-neutral-700 dark:text-neutral-300 shadow-sm',
  'hover:border-neutral-300 dark:hover:border-white/20 hover:bg-neutral-50 dark:hover:bg-white/5',
  'data-[active]:border-primary-300 dark:data-[active]:border-primary-500/30 data-[active]:bg-primary-50 dark:data-[active]:bg-primary-500/10 data-[active]:text-primary-900 dark:data-[active]:text-primary-200'
);

const PANEL_CLASS = clsx(
  'z-20 mt-1 w-80 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-4 py-3 shadow-lg ring-1 ring-black/5 dark:ring-white/10',
  'origin-top transition data-[closed]:scale-95 data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in'
);

function formatRateLabel(rate: number | undefined): string {
  if (rate == null || rate <= 0) return '';
  return `$${rate % 1 === 0 ? rate : rate.toFixed(2)}/hr`;
}

function formatAccountPremiumLabel(amount: number | undefined): string {
  if (amount == null || amount <= 0) return '';
  if (amount >= 1000) {
    const k = amount / 1000;
    return `$${k % 1 === 0 ? k : k.toFixed(1)}k account`;
  }
  return `$${amount} account`;
}

function assumptionsSummary(assumptions: AnalyticsAssumptions): string {
  const ratePart = formatRateLabel(assumptions.hourlyRate);
  const accountPart = formatAccountPremiumLabel(assumptions.avgAccountPremium);
  if (ratePart && accountPart) return `${ratePart} · ${accountPart}`;
  if (ratePart) return ratePart;
  if (accountPart) return accountPart;
  return 'Set assumptions';
}

function parsePositiveNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

export const AnalyticsAssumptionsPicker = ({
  assumptions,
  setHourlyRate,
  setAvgAccountPremium
}: {
  assumptions: AnalyticsAssumptions;
  setHourlyRate: (rate: number | undefined) => void;
  setAvgAccountPremium: (amount: number | undefined) => void;
}) => {
  const [hourlyDraft, setHourlyDraft] = React.useState(
    assumptions.hourlyRate != null ? String(assumptions.hourlyRate) : ''
  );
  const [accountDraft, setAccountDraft] = React.useState(
    assumptions.avgAccountPremium != null ? String(assumptions.avgAccountPremium) : ''
  );

  React.useEffect(() => {
    setHourlyDraft(assumptions.hourlyRate != null ? String(assumptions.hourlyRate) : '');
  }, [assumptions.hourlyRate]);

  React.useEffect(() => {
    setAccountDraft(
      assumptions.avgAccountPremium != null ? String(assumptions.avgAccountPremium) : ''
    );
  }, [assumptions.avgAccountPremium]);

  const isFiltered =
    (assumptions.hourlyRate != null && assumptions.hourlyRate > 0) ||
    (assumptions.avgAccountPremium != null && assumptions.avgAccountPremium > 0);

  const commitHourly = () => {
    setHourlyRate(parsePositiveNumber(hourlyDraft));
  };

  const commitAccountPremium = () => {
    const parsed = parsePositiveNumber(accountDraft);
    setAvgAccountPremium(parsed != null ? Math.round(parsed) : undefined);
  };

  return (
    <Headless.Popover className="relative">
      {({ close }) => (
        <>
          <Headless.PopoverButton
            className={TRIGGER_CLASS}
            data-active={isFiltered ? '' : undefined}
          >
            <AdjustmentsHorizontalIcon className="size-4" />
            <span>{assumptionsSummary(assumptions)}</span>
            <ChevronDownIcon className="size-3.5 text-neutral-400 dark:text-neutral-500" />
          </Headless.PopoverButton>
          <Headless.PopoverPanel transition anchor="bottom start" className={PANEL_CLASS}>
            <div className="pb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Assumptions
            </div>
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                  Hourly rate ($/hr)
                </span>
                <p className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                  Used to convert hours saved into dollars.
                </p>
                <input
                  type="number"
                  min={0}
                  step={1}
                  placeholder={String(DEFAULT_ANALYTICS_HOURLY_RATE)}
                  value={hourlyDraft}
                  onChange={(e) => setHourlyDraft(e.target.value)}
                  onBlur={commitHourly}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      commitHourly();
                      close();
                    }
                  }}
                  className="mt-1.5 w-full rounded-lg border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 text-sm text-neutral-900 dark:text-white shadow-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                  Average account premium ($)
                </span>
                <p className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                  Used to estimate incremental revenue per closed account.
                </p>
                <input
                  type="number"
                  min={0}
                  step={100}
                  placeholder={String(DEFAULT_ANALYTICS_AVG_ACCOUNT_PREMIUM)}
                  value={accountDraft}
                  onChange={(e) => setAccountDraft(e.target.value)}
                  onBlur={commitAccountPremium}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      commitAccountPremium();
                      close();
                    }
                  }}
                  className="mt-1.5 w-full rounded-lg border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 text-sm text-neutral-900 dark:text-white shadow-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
              </label>
            </div>
            <div className="mt-3 flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded-md px-2 py-1 text-xs text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-white/5"
                onClick={() => {
                  setHourlyDraft('');
                  setAccountDraft('');
                  setHourlyRate(undefined);
                  setAvgAccountPremium(undefined);
                  close();
                }}
              >
                Clear
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700"
                onClick={() => {
                  commitHourly();
                  commitAccountPremium();
                  close();
                }}
              >
                <CheckIcon className="size-3.5" />
                Apply
              </button>
            </div>
          </Headless.PopoverPanel>
        </>
      )}
    </Headless.Popover>
  );
};
